import type {
  ApprovalDiff,
  ApprovalRule,
  PermissionCell,
  PermissionCellChange,
  PermissionDiff,
} from '@felix/domain';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import { roleLabel } from './RolesScreen.js';
import classes from './Setup.module.css';
import type { MatrixRoleItem } from './setup-types.js';

type T = TFunction;

export function roleNameOf(t: T, roles: readonly MatrixRoleItem[], id: string): string {
  const role = roles.find((r) => r.id === id);
  return role ? roleLabel(t, role) : id;
}

/** Hücrenin okunur adı: rol · modül · işlem (· konu). */
export function cellText(
  t: T,
  roles: readonly MatrixRoleItem[],
  cell: Pick<PermissionCell, 'roleId' | 'module' | 'action' | 'subject'>,
  subjectLabel: (subject: string) => string = (s) => s,
): string {
  return [
    roleNameOf(t, roles, cell.roleId),
    t(`setup.matrix.moduleNames.${cell.module}`),
    t(`setup.matrix.actions.${cell.action}`),
    ...(cell.subject ? [subjectLabel(cell.subject)] : []),
  ].join(' · ');
}

/** Kuralın okunur özeti: tür (konu), giren → adımlar. */
export function ruleText(
  t: T,
  roles: readonly MatrixRoleItem[],
  rule: ApprovalRule,
  subjectLabel: (subject: string) => string = (s) => s,
): string {
  const entered =
    rule.enteredBy === 'any'
      ? t('setup.matrix.rules.any')
      : rule.enteredBy.map((id) => roleNameOf(t, roles, id)).join(', ');
  const steps =
    rule.steps.length === 0
      ? t('setup.matrix.rules.noStep')
      : rule.steps
          .map(
            (id, i) =>
              `${roleNameOf(t, roles, id)}${rule.officeSteps?.[i] ? ` (${t('setup.matrix.rules.office')})` : ''}`,
          )
          .join(' → ');
  const band = [
    rule.minAmount !== undefined ? t('setup.matrix.rules.from', { amount: rule.minAmount }) : null,
    rule.maxAmount !== undefined ? t('setup.matrix.rules.to', { amount: rule.maxAmount }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  return [
    t(`setup.matrix.recordTypes.${rule.recordType}`),
    rule.subject ? subjectLabel(rule.subject) : null,
    band || null,
    `${entered} → ${steps}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function Item({
  tone,
  label,
  text,
}: {
  tone: 'success' | 'danger' | 'warning';
  label: string;
  text: string;
}) {
  return (
    <li className={classes.diffItem} data-tone={tone}>
      <StatusBadge tone={tone}>{label}</StatusBadge>
      <span>{text}</span>
    </li>
  );
}

/**
 * İki matris sürümü (ya da taslak ve güncel sürüm) arasındaki fark (ADR 0042 §24): eklenen
 * (yeşil), kaldırılan (kırmızı) ve değişen (sarı) hücreler, gerekçeler ve onay kuralları. Renkler
 * token'lardan; renk tek işaret değildir, rozet metni de yazar.
 */
export function MatrixDiff({
  roles,
  permissions,
  approvals,
  subjectLabel,
}: {
  roles: readonly MatrixRoleItem[];
  permissions: PermissionDiff;
  approvals: ApprovalDiff;
  subjectLabel?: (subject: string) => string;
}) {
  const { t } = useTranslation();
  const d = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.diff.${key}`, options);
  const scope = (value: string) => t(`setup.matrix.scope.${value}`);
  const cell = (c: PermissionCell | PermissionCellChange) => cellText(t, roles, c, subjectLabel);
  const empty =
    permissions.added.length +
      permissions.removed.length +
      permissions.changed.length +
      permissions.justifications.length +
      approvals.added.length +
      approvals.removed.length +
      approvals.changed.length ===
    0;
  if (empty) return <p className={classes.muted}>{d('none')}</p>;
  return (
    <div>
      {permissions.added.length + permissions.removed.length + permissions.changed.length > 0 ? (
        <section className={classes.diffSection} aria-label={d('permissions')}>
          <h3 className={classes.diffTitle}>
            {d('permissions')}{' '}
            <span className={classes.muted}>
              {d('summary', {
                added: permissions.added.length,
                removed: permissions.removed.length,
                changed: permissions.changed.length,
              })}
            </span>
          </h3>
          <ul className={classes.diffList}>
            {permissions.added.map((c) => (
              <Item
                key={`a-${cell(c)}`}
                tone="success"
                label={d('added')}
                text={`${cell(c)}: ${scope(c.scope)}`}
              />
            ))}
            {permissions.removed.map((c) => (
              <Item
                key={`r-${cell(c)}`}
                tone="danger"
                label={d('removed')}
                text={`${cell(c)}: ${scope(c.scope)}`}
              />
            ))}
            {permissions.changed.map((c) => (
              <Item
                key={`c-${cell(c)}`}
                tone="warning"
                label={d('changed')}
                text={`${cell(c)}: ${d('fromTo', { from: scope(c.from), to: scope(c.to) })}`}
              />
            ))}
          </ul>
        </section>
      ) : null}
      {permissions.justifications.length > 0 ? (
        <section className={classes.diffSection} aria-label={d('justifications')}>
          <h3 className={classes.diffTitle}>{d('justifications')}</h3>
          <ul className={classes.diffList}>
            {permissions.justifications.map((j) => {
              const [rule, roleId] = j.key.split(':');
              const text = `${roleNameOf(t, roles, roleId ?? '')} · ${rule}: ${j.to ?? j.from ?? ''}`;
              const tone = j.from === null ? 'success' : j.to === null ? 'danger' : 'warning';
              const label = d(j.from === null ? 'added' : j.to === null ? 'removed' : 'changed');
              return <Item key={j.key} tone={tone} label={label} text={text} />;
            })}
          </ul>
        </section>
      ) : null}
      {approvals.added.length + approvals.removed.length + approvals.changed.length > 0 ? (
        <section className={classes.diffSection} aria-label={d('approvals')}>
          <h3 className={classes.diffTitle}>
            {d('approvals')}{' '}
            <span className={classes.muted}>
              {d('summary', {
                added: approvals.added.length,
                removed: approvals.removed.length,
                changed: approvals.changed.length,
              })}
            </span>
          </h3>
          <ul className={classes.diffList}>
            {approvals.added.map((rule, i) => (
              <Item
                // biome-ignore lint/suspicious/noArrayIndexKey: kuralların kimliği yok
                key={`a-${i}`}
                tone="success"
                label={d('added')}
                text={ruleText(t, roles, rule, subjectLabel)}
              />
            ))}
            {approvals.removed.map((rule, i) => (
              <Item
                // biome-ignore lint/suspicious/noArrayIndexKey: kuralların kimliği yok
                key={`r-${i}`}
                tone="danger"
                label={d('removed')}
                text={ruleText(t, roles, rule, subjectLabel)}
              />
            ))}
            {approvals.changed.map((change) => (
              <Item
                key={change.key}
                tone="warning"
                label={d('changed')}
                text={`${ruleText(t, roles, change.from, subjectLabel)} ⇒ ${ruleText(t, roles, change.to, subjectLabel)}`}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
